import { clearPendingScheduleDate } from '@platewell/core';
import {
  useCollections,
  useConnectionStatus,
  useFormatTag,
  usePlatewell,
  useRecipeQuota,
  useTags,
} from '@platewell/query';
import { Link, useRouterState } from '@tanstack/react-router';
import { signOut } from '../../lib/api.js';
import { useAdminAccess } from '../../lib/session.js';

/**
 * The pieces of navigation shared by the desktop sidebar and the mobile More sheet.
 *
 * Each section reads its own data rather than taking it as props: the sidebar and the sheet
 * are never mounted at the same time, so there is no duplicated subscription, and a section
 * can be dropped into either place without the shell knowing what it needs.
 *
 * `onNavigate` is how the sheet closes itself when a link is followed — including the case
 * where the link points at the page already open, which no route change would report.
 */
export interface NavSectionProps {
  onNavigate?: () => void;
}

export function NavItem({
  to,
  label,
  active,
  onNavigate,
}: {
  to: string;
  label: string;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      className={`nav-item ${active ? 'active' : ''}`}
      to={to}
      onClick={() => {
        onNavigate?.();
      }}
    >
      {label}
    </Link>
  );
}

/**
 * Hidden for viewers, who cannot add recipes to the household in the first place.
 *
 * At the plan limit it stays visible and points at the paywall instead. An entry that
 * vanished would leave someone hunting for a feature they used yesterday with no
 * explanation of where it went.
 */
export function ImportNavItem({ onNavigate }: NavSectionProps) {
  const { readOnly } = useConnectionStatus();
  const quota = useRecipeQuota();
  const pathname = usePathname();
  if (readOnly) return null;

  return (
    <NavItem
      to={quota.atLimit ? '/subscribe' : '/imports'}
      label="Import Recipes"
      active={pathname === '/imports' || pathname.startsWith('/imports/')}
      onNavigate={onNavigate}
    />
  );
}

/**
 * Open to viewers too: browsing what was added lately needs no write access.
 *
 * Hidden until the household has a recipe, since an empty book has nothing recent to show.
 */
export function RecentNavItem({ onNavigate }: NavSectionProps) {
  const quota = useRecipeQuota();
  const pathname = usePathname();
  if (quota.used === 0) return null;

  return (
    <NavItem
      to="/recent"
      label="Recently Added"
      active={pathname === '/recent'}
      onNavigate={onNavigate}
    />
  );
}

/** The destinations that become tabs on a phone, so this is the sidebar's share of them. */
export function PrimaryNavLinks({ onNavigate }: NavSectionProps) {
  const pathname = usePathname();

  return (
    <>
      <NavItem
        to="/"
        label="All Recipes"
        active={pathname === '/'}
        onNavigate={() => {
          clearPendingScheduleDate();
          onNavigate?.();
        }}
      />
      <NavItem
        to="/favourites"
        label="Favourites"
        active={pathname === '/favourites'}
        onNavigate={onNavigate}
      />
      <NavItem
        to="/schedule"
        label="Schedule"
        active={pathname === '/schedule'}
        onNavigate={onNavigate}
      />
      <ImportNavItem onNavigate={onNavigate} />
      <RecentNavItem onNavigate={onNavigate} />
    </>
  );
}

export function CollectionsNav({ onAdd, onNavigate }: NavSectionProps & { onAdd: () => void }) {
  const { store } = usePlatewell();
  const { readOnly } = useConnectionStatus();
  const collections = useCollections();
  const pathname = usePathname();

  return (
    <>
      <div className="nav-heading-row">
        <span className="nav-heading">Collections</span>
        {!readOnly && store != null && (
          <button type="button" className="nav-heading-link" onClick={onAdd}>
            add
          </button>
        )}
      </div>
      {collections.map((collection) => (
        <Link
          key={collection.id}
          className={`nav-item ${pathname === `/collections/${collection.id}` ? 'active' : ''}`}
          to="/collections/$collectionId"
          params={{ collectionId: collection.id }}
          onClick={() => onNavigate?.()}
        >
          {collection.name}
          <span className="nav-count">{collection.recipeCount}</span>
        </Link>
      ))}
    </>
  );
}

export function TagsNav({ onNavigate }: NavSectionProps) {
  const tags = useTags(8);
  const formatTag = useFormatTag();
  if (tags.length === 0) return null;

  return (
    <>
      <span className="nav-heading">Tags</span>
      {tags.map((facet) => (
        <Link
          key={facet.tag}
          className="nav-item"
          to="/"
          search={{ tag: facet.tag }}
          activeOptions={{ includeSearch: true }}
          activeProps={{ className: 'nav-item active' }}
          onClick={() => onNavigate?.()}
        >
          {formatTag(facet.tag)}
          <span className="nav-count">{facet.count}</span>
        </Link>
      ))}
    </>
  );
}

/**
 * Admin, the personal links and the way out.
 *
 * Sign out reloads rather than navigating, so the household manager, its sockets and every
 * cached document are torn down instead of being left holding a session that is gone.
 */
export function AccountNav({ onNavigate }: NavSectionProps) {
  const { canAdmin } = useAdminAccess();
  const pathname = usePathname();

  return (
    <>
      {/* Never active: neither shell that renders this is mounted under /admin. */}
      {canAdmin && <NavItem to="/admin" label="Admin" active={false} onNavigate={onNavigate} />}
      <NavItem
        to="/profile"
        label="Profile"
        active={pathname === '/profile'}
        onNavigate={onNavigate}
      />
      <NavItem
        to="/settings"
        label="Household settings"
        active={pathname === '/settings'}
        onNavigate={onNavigate}
      />
      <button
        className="nav-item"
        onClick={() => {
          void signOut().then(() => window.location.reload());
        }}
      >
        Sign out
      </button>
    </>
  );
}

function usePathname(): string {
  return useRouterState({ select: (state) => state.location.pathname });
}
